import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { TrendingUp, Award } from 'lucide-react';

export default function TipsChart() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(8);
  const isManager = user?.role === 'manager' || user?.role === 'director';

  const fetchData = useCallback(async () => {
    try { const { data } = await api.get(`/staff/payroll/tips-stats?weeks=${weeks}`); setData(data); } catch (err) { console.error('fetch tips stats failed:', err); }
  }, [weeks]);

  useEffect(() => { if (isManager) fetchData(); }, [fetchData, isManager]);

  if (!isManager || !data) return null;
  const max = Math.max(...data.weeks.map(w => w.total_tips), 1);

  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4" data-testid="tips-chart">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-sans text-white font-medium">Tips Collected</h3>
          <span className="text-zinc-500 font-sans text-xs">last {weeks} weeks</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-emerald-400 font-sans font-semibold text-base" data-testid="tips-grand-total">${data.grand_total_tips.toFixed(2)}</span>
          <select value={weeks} onChange={e => setWeeks(parseInt(e.target.value))} className="bg-transparent border border-white/10 text-zinc-400 font-sans text-xs rounded-sm px-2 py-1 focus:outline-none" data-testid="tips-weeks-select">
            <option value="4">4 weeks</option>
            <option value="8">8 weeks</option>
            <option value="12">12 weeks</option>
            <option value="26">6 months</option>
          </select>
        </div>
      </div>

      {/* Bar chart */}
      <div className="flex items-end gap-1.5 h-32 mb-3">
        {data.weeks.map((w, i) => {
          const heightPct = max > 0 ? (w.total_tips / max) * 100 : 0;
          return (
            <div key={w.week_label || i} className="flex-1 flex flex-col items-center gap-1 group" data-testid={`tips-bar-${i}`}>
              <div className="w-full flex-1 flex items-end">
                <div
                  className="w-full bg-gradient-to-t from-emerald-500/50 to-emerald-400/80 rounded-t-sm transition-all hover:from-emerald-400 hover:to-emerald-300 relative"
                  style={{ height: `${heightPct}%`, minHeight: w.total_tips > 0 ? '4px' : '0px' }}
                  title={`$${w.total_tips} from ${w.tip_orders} tipped orders`}
                >
                  {w.total_tips > 0 && (
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-sans text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      ${w.total_tips}
                    </span>
                  )}
                </div>
              </div>
              <span className="text-[10px] font-sans text-zinc-500 truncate w-full text-center">{w.week_label}</span>
            </div>
          );
        })}
      </div>

      {/* Leaderboard */}
      {data.leaderboard.length > 0 && (
        <div className="border-t border-white/5 pt-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans font-bold mb-2 flex items-center gap-1.5">
            <Award className="w-3 h-3 text-gold" /> Top Earners (Tips Pool)
          </p>
          <div className="space-y-1">
            {data.leaderboard.slice(0, 5).map((s, idx) => (
              <div key={s.user_name} className="flex items-center gap-2 text-xs font-sans" data-testid={`leaderboard-${idx}`}>
                <span className={`w-5 text-center font-bold ${idx === 0 ? 'text-gold' : idx === 1 ? 'text-zinc-300' : idx === 2 ? 'text-amber-700' : 'text-zinc-600'}`}>
                  {idx + 1}
                </span>
                <span className="flex-1 text-white truncate">{s.user_name} <span className="text-zinc-600 capitalize">({s.role})</span></span>
                <span className="text-emerald-400 font-medium">${s.total_tips.toFixed(2)}</span>
                <span className="text-zinc-500 text-[10px] w-12 text-right">{s.weeks_paid}w paid</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.grand_total_tips === 0 && (
        <p className="text-center text-zinc-600 font-sans text-xs py-4">No tips collected yet. Tips will appear once card payments with gratuity are completed.</p>
      )}
    </div>
  );
}
